import type { ExtendedISbStoryData } from '~/types/storyblokApi';
import type { ISbStoryParams } from '@storyblok/js';
import { useCurrentSectionConfig } from '../useCurrentSectionConfig';
import { reuseSsrPayloadOnServer } from '~/utils/reuseSsrPayload';
import type { RouteLocationNormalizedGeneric } from 'vue-router';
import type { MaybeRefOrGetter } from 'vue';

interface Options {
    /** If set to true, the country segment will not be added to the URL. */
    disableCountrySegment?: boolean;
    /** If set to true, errors during the fetch will not throw a fatal error. */
    disableFatalErrors?: boolean;
    enrichLinkedStories?: 'locked';
    /** If set to false, the current section config will not be fetched alongside the requested story. */
    includeSectionConfig?: boolean;
    key?: MaybeRefOrGetter<string>;
    route?: RouteLocationNormalizedGeneric;
}

/**
 * Fetches any arbitrary single story from Storyblok based on its slug.
 *
 * For fetching multiple stories, use `useStories`. For fetching the current config, use `useConfig`. For
 * fetching the global config, use `useGlobalConfig`. For fetching datasource entries, use `useDatasourceEntries`.
 *
 * **Example:**
 * ```ts
 * import { useStory } from '~/composables/storyblok-proxy/useStory';
 *
 * const { rawPath } = useCleanStoryblokPath();
 * const { data: story } = await useStory<PageComponentStoryblok>(rawPath);
 * useStoryblokBridgeRef(story);
 * ```
 *
 * @param slug - The slug of the story to fetch. Don't add the app folder or country segment, they are added automatically unless disabled via options.
 * @param params - Optional Storyblok story parameters.
 * @param options - Optional settings. See {@link Options} for more details.
 * @returns A reactive object containing the fetched story data, loading state, and error state.
 * @throws Will throw an fatal error if the fetch fails, unless `disableFatalErrors` is set to true in options.
 */
export const useStory = <T>(
    slug: Ref<string> | string,
    params?: Ref<ISbStoryParams> | ISbStoryParams | null,
    options?: Options
) => {
    const { lang, country } = useLanguage(options?.route);

    const slugRef = toRef(slug);
    const _slug = computed(() => {
        const segments = [];
        if (!options?.disableCountrySegment) segments.push(country.value);
        segments.push(slugRef.value.replace(/^\/+/, ''));
        return segments.join('/');
    });

    const paramsRef = toRef(params);
    const stringifiedParams = computed(() => JSON.stringify(paramsRef.value));
    const headers = useRequestHeaders(['cookie']);

    const matchedConfig = useCurrentSectionConfig(options?.route);
    const sectionConfigSlug = computed(() => {
        if (options?.includeSectionConfig === false) {
            return null;
        }

        const configSlug = matchedConfig.value?.slug;
        if (!configSlug) {
            return null;
        }
        return `${country.value}/${configSlug.replace(/^\/+/, '')}`;
    });

    const result = useFetch<ExtendedISbStoryData<T>>(StoryblokProxyEndpoint, {
        headers,
        key: options?.key,
        query: {
            type: 'story',
            slug: _slug,
            language: lang,
            enrichLinkedStories: options?.enrichLinkedStories,
            params: stringifiedParams,
            sectionConfigSlug
        },
        // `dedupe` only joins calls that overlap in time; `getCachedData` is what
        // makes a second caller later in the same render reuse the first response.
        dedupe: 'defer',
        getCachedData: reuseSsrPayloadOnServer
    });

    watch(result.error, (error) => {
        if (!error) return;
        if (options?.disableFatalErrors) return;

        throw createError({
            statusCode: error.statusCode,
            message: error.message,
            fatal: true
        });
    });

    return result;
};
